/**
 * Benefit plan groups API client functions
 *
 * Low-level API functions for benefit plan group management.
 * These handle the HTTP requests and are consumed by React Query hooks.
 */

import { AxiosInstance } from "axios";

import { BenefitPlanGroup } from "../types/models";

// Base URL for benefit plan group APIs
const BENEFIT_PLAN_GROUPS_BASE_URL = "btx/v1/benefit-plan-groups";

export interface ListBenefitPlanGroupsParams {
  rfpId: string;
  coverageType: string;
  productType?: string;
}

/** API response wrapper for list endpoint */
interface ListBenefitPlanGroupsResponse {
  benefitPlanGroups: BenefitPlanGroup[];
  isComplete: boolean;
}

/**
 * Lists benefit plan groups for an RFP and coverage type
 *
 * GET /api/btx/v1/benefit-plan-groups?rfpId={id}&coverageType={type}&productType={type}
 *
 * @param params - Query parameters (rfpId required, coverageType required, productType optional)
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to list of benefit plan groups
 */
export const listBenefitPlanGroups = async (
  params: ListBenefitPlanGroupsParams,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanGroup[]> => {
  const queryParams = new URLSearchParams();
  queryParams.set("rfpId", params.rfpId);
  queryParams.set("coverageType", params.coverageType);
  if (params.productType) {
    queryParams.set("productType", params.productType);
  }

  const response = await axiosInstance.get<ListBenefitPlanGroupsResponse>(
    `${BENEFIT_PLAN_GROUPS_BASE_URL}?${queryParams.toString()}`,
  );

  return response.data?.benefitPlanGroups ?? [];
};

/**
 * Gets a single benefit plan group by ID
 *
 * GET /api/btx/v1/benefit-plan-groups/:id
 *
 * @param groupId - Benefit plan group ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to benefit plan group
 */
export const getBenefitPlanGroup = async (
  groupId: string,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanGroup> => {
  const response = await axiosInstance.get<BenefitPlanGroup>(
    `${BENEFIT_PLAN_GROUPS_BASE_URL}/${groupId}`,
  );

  return response.data;
};

/** Per-plan premium rollup returned by the premiums endpoint. */
export interface BenefitPlanPremium {
  id: string;
  monthlyPremium?: number;
  annualPremium?: number;
}

/** Premium-only rollup for a single benefit plan group. */
export interface BenefitPlanGroupPremiums {
  id: string;
  totalMonthlyPremium: number;
  totalAnnualPremium: number;
  benefitPlans: BenefitPlanPremium[];
}

/**
 * Gets only the on-demand premium rollups for a benefit plan group.
 *
 * GET /api/btx/v1/benefit-plan-groups/:id/premiums
 *
 * Lets the rates summary refresh premiums after an inline rate edit without
 * refetching the full group payload, which would remount the editable rates
 * grid and wipe in-flight edits (XTRM-2024).
 *
 * @param groupId - Benefit plan group ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to the group's premium rollups
 */
export const getBenefitPlanGroupPremiums = async (
  groupId: string,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanGroupPremiums> => {
  const response = await axiosInstance.get<BenefitPlanGroupPremiums>(
    `${BENEFIT_PLAN_GROUPS_BASE_URL}/${groupId}/premiums`,
  );

  return response.data;
};
